<template>
  <div>
    <div class="course-detail-page" v-if="detail">
      <exam-header :canClickLogo="true"></exam-header>
      <div class="course-detail-title">{{ chapterTitle }}</div>
      <section class="course-content">
        <div class="video-column">
          <course-video-player
            :chapterTitle="chapterTitle"
            :chapters="chapters"
            :current-index="currentIndex"
            :resume-time="resumeTime"
            :show-resume-tip="showResumeTip"
            @update:currentIndex="handleChapterChange"
            @resume="handleResume"
            @fullscreenchange="scrollChapterListToCurrent"
          />
        </div>

        <aside class="chapter-column">
          <section class="chapter-info">
            <img :src="detail.baseInfo?.coverImageRes" class="chapter-info_cover" />
            <div class="chapter-info_cnt">
              <h3 class="chapter-info_title"><span class="chapter-info_tag">免费</span>{{ detail.baseInfo?.title }}</h3>
              <div class="chapter-info_desc">授课老师：{{ detail.baseInfo?.author || '-' }}</div>
            </div>
          </section>
          <section class="chapter-cnt" ref="chapterListBody">
            <div class="chapter-header">
              <div class="chapter-header_title">课时列表</div>
              <span>共 {{ chapters.length }} 节课</span>
            </div>
            <ul class="chapter-list">
              <li
                v-for="(chapter, index) in chapters"
                :key="index"
                :class="['chapter-item', { active: index === currentIndex }]"
                @click="handleChapterChange(index)"
              >
              <span class="chapter-item_title">
                {{ chapter.title }}
              </span>
              <span>{{ chapter.fragment | formatTime }}</span>
              </li>
            </ul>
          </section>
        </aside>
      </section>
    </div>
    <div v-else class="course-detail-page loading">
      <text-loading title="正在加载课程详情..."></text-loading>
    </div>
    <page-footer></page-footer>
  </div>
</template>

<script>
import { getCourseDetail } from '@/api/courseData';
import ExamHeader from "@/components/examHeader.vue";
import PageFooter from "@/components/PageFooter.vue";
import CourseVideoPlayer from './components/CourseVideoPlayer.vue';
import TextLoading from '@/components/loading.vue';
import { formatDuration } from '@/common/js/util';
import { getUser } from '@/api/user';

export default {
  name: 'CourseDetailPage',
  components: {
    CourseVideoPlayer,
    ExamHeader,
    TextLoading,
    PageFooter,
  },
  data() {
    return {
      detail: null,
      chapters: [],
      currentIndex: 0,
      resumeTime: 0,
      showResumeTip: false,
    };
  },
  computed: {
    chapterTitle() {
      const courseTitle = this.detail?.baseInfo?.title || '课程';
      const chapterTitle = this.chapters[this.currentIndex]?.title || '课时';
      return `${courseTitle}：${chapterTitle}`;
    }
  },
  filters: {
    formatTime(seconds) {
      return formatDuration(seconds);
    }
  },
  async mounted() {
    await getUser()
    this.fetchDetail();
  },
  methods: {
    fetchDetail() {
      const id = Number(this.$route?.query?.libraryId) || 15170225;
      const chapterIdFromUrl = this.$route?.query?.chapterId;
      this.detail = null;
      this.resumeTime = 0;
      this.showResumeTip = false;

      getCourseDetail(id).then((res) => {
        this.detail = res.body;
        this.chapters = res.body?.catalogue?.chapters || [];

        if (this.chapters.length) {
          let targetIndex = 0;
          // 优先检查 URL 参数中的 chapterId
          if (chapterIdFromUrl) {
            const index = this.chapters.findIndex(
              (ch) => ch.chapterId === chapterIdFromUrl
            );
            if (index >= 0) {
              targetIndex = index;
              const chapter = this.chapters[index];
              if (chapter.readFragment > 0 && chapter.readFragment < chapter.fragment - 5) { // 从记录继续播放
                this.resumeTime = chapter.readFragment;
                this.showResumeTip = true;
              }
            }
          } else {
            // 如果没有 URL 参数，检查上次播放进度
            const readProgress = res.body?.userPreference?.readProgress;
            if (readProgress && readProgress.chapterId) {
              const index = this.chapters.findIndex(
                (ch) => ch.chapterId === readProgress.chapterId
              );
              if (index >= 0) {
                if (!readProgress.fragment || readProgress.fragment < 5) {
                  targetIndex = index;
                } else {
                  const chapter = this.chapters[index];
                  if (readProgress.fragment < chapter.fragment - 5) { // 从记录继续播放
                    targetIndex = index;
                    this.resumeTime = readProgress.fragment;
                    this.showResumeTip = true;
                  } else {
                    targetIndex = index === this.chapters.length - 1 ? 0 : index + 1;
                  }
                }
              }
            }
          }

          this.currentIndex = targetIndex;
        }
        this.scrollChapterListToCurrent();
      }).catch((res) => {
        this.$confirm(res.message || '没有找到资源', '加载失败', {
          cancelButtonText: '刷新页面',
          confirmButtonText: '返回列表',
          type: 'error',
          customClass: 'qz-message-box',
          showClose: false,
          closeOnPressEscape: false,
          closeOnClickModal: false,
        }).then(() => {
          this.$router.push({ name: 'contentIndex'})
        }).catch(() => {
          window.location.reload()
        });
      })
    },
    handleChapterChange(index) {
      if (index === this.currentIndex) return;
      if (index < 0 || index >= this.chapters.length) return;
      this.currentIndex = index;
      // 切换章节时重置恢复播放提示
      this.resumeTime = 0;
      this.showResumeTip = false;
      this.scrollChapterListToCurrent();
    },
    handleResume(time) {
      // 用户点击继续播放后，隐藏提示
      this.showResumeTip = false;
    },
    scrollChapterListToCurrent() {
      this.$nextTick(() => {
        const container = this.$refs.chapterListBody;
        if (!container) return;
        const active = container.querySelector('.chapter-item.active');
        if (!active) {
          container.scrollTop = 0;
          return;
        }
        const offset = active.offsetTop - container.clientHeight / 2 + active.clientHeight;
        container.scrollTop = Math.max(0, offset);
      })
    },
  },
};
</script>

<style scoped lang="less">
.course-detail-page {
  background: #EDE4DA;
  height: 100vh;
  box-sizing: border-box;
  padding: 76px 20px 0;
  text-align: left;
  display: flex;
  flex-direction: column;
}

.course-detail-page.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}
.course-detail-title {
  color: #1D2129;
  font-size: 20px;
  line-height: 72px;
  font-weight: 600;
}
.course-content {
  display: flex;
  gap: 16px;
  flex: 1;
  overflow: hidden;
}

.chapter-column {
  width: 360px;
  background: #fff;
  // border-radius: 16px 16px 0 0;
  // height: 100%;
  height: calc(100% - 32px);
  border-radius: 16px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  .chapter-info {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 20px;
    line-height: 1.5;
    &_cover {
      width: 148px;
      height: 99px;
      object-fit: cover;
      border-radius: 10px;
    }
    &_title {
      // display: flex;
      // align-items: flex-start;
      color: #000000E5;
      font-size: 20px;
      font-weight: 600;
      line-height: 1.5;
    }
    &_desc {
      color: #4E5969;
      font-size: 14px;
    }
    &_tag {
      display: inline-block;
      background: #14C67C;
      color: #fff;
      border-radius: 4px;
      padding: 1px 6px;
      font-size: 12px;
      font-weight: 500;
      margin-right: 6px;
      white-space: nowrap;
      vertical-align: middle;
    }
  }
  .chapter-cnt {
    background: #F7F6F5;
    padding: 20px;
    flex: 1;
    overflow: auto;
    position: relative;
    .chapter-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
      padding-bottom: 12px;
      border-bottom: 1px solid #e6e6e6;
      &_title {
        color: #000000E5;
        font-size: 18px;
        font-weight: 600;
      }
    }
    .chapter-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .chapter-item {
      border: 1px solid #E5E6EB;
      border-radius: 4px;
      padding: 12px 16px;
      cursor: pointer;
      background: #fff;
      transition: all 0.2s ease;
      font-weight: 500;
      font-size: 14px;
      color: #1D2129;
      display: flex;
      justify-content: space-between;
      align-items: center;
      &_title {
        flex: 1;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
      }
      &:hover {
        border-color: #E600124D;
      }
    }
    .chapter-item.active {
      border-color: #E600124D;
      color: #E60012;
    }
  }
}

.video-column {
  flex: 1;
  height: calc(100% - 32px);
  // max-height: 846px;
}


@media (max-width: 1024px) {
  .course-detail-page {
    height: auto;
  }
  .course-content {
    flex-direction: column;
    overflow: auto;
  }
  .chapter-column {
    width: 100%;
    height: auto;
  }
}
</style>
